import { ChevronRight, MessageCircle, Star, TrendingUp, Award, ArrowRight, Sparkles, Headphones } from "lucide-react";
import { IMAGES, LINE_URL, EARLYBIRD_TOTAL, EARLYBIRD_LEFT } from "../constants";

const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-[90vh] md:min-h-screen bg-white overflow-hidden pt-20 md:pt-32">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#FF6B00] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#0047FF] rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-5 md:px-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center md:min-h-[80vh]">
          <div className="space-y-5 md:space-y-8 animate-fade-in-up">
            <h1 className="text-[2.25rem] leading-[1.15] sm:text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight">
              包棟民宿
              <span className="text-gradient block">一條龍營銷代管服務</span>
            </h1>

            {/* Stylish subtitle pill row */}
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="inline-flex items-center gap-1.5 bg-[#0A2540] text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-bold">
                <Sparkles className="w-3.5 h-3.5 text-[#F5C24A]" />
                AI × KOL × PMS
              </span>
              <span className="inline-flex items-center gap-1.5 bg-[#FF6B00]/10 text-[#FF6B00] px-3 py-1.5 rounded-full text-xs md:text-sm font-bold border border-[#FF6B00]/30">
                <Headphones className="w-3.5 h-3.5" />
                13hrs 專人接單
              </span>
              <span className="inline-flex items-center gap-1.5 bg-[#F5C24A]/10 text-[#B8860B] px-3 py-1.5 rounded-full text-xs md:text-sm font-bold border border-[#F5C24A]/40">
                <Award className="w-3.5 h-3.5" />
                多場域實績見證
              </span>
            </div>

            {/* Early Bird Highlight Banner */}
            <a href="#pricing" data-testid="hero-earlybird-badge"
              className="group flex items-center gap-3 bg-gradient-to-r from-[#F5C24A] via-[#E0A030] to-[#F5C24A] text-[#0A2540] px-4 py-3 rounded-2xl shadow-lg shadow-[#F5C24A]/30 hover:shadow-xl transition-all max-w-md">
              <div className="w-10 h-10 rounded-full bg-[#0A2540] flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-[#F5C24A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider opacity-80">早鳥限量優惠</span>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600"></span>
                  </span>
                  <span className="text-[10px] md:text-xs font-black text-red-700">
                    <span className="line-through opacity-60">限 {EARLYBIRD_TOTAL}</span> 剩 {EARLYBIRD_LEFT} 位
                  </span>
                </div>
                <div className="text-sm md:text-base font-black leading-tight">早鳥享<span className="text-[#FF6B00]">見證場保障方案</span>，更安心更有保障</div>
              </div>
              <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </a>

            <p className="text-base md:text-lg text-[#475569] leading-relaxed max-w-lg">
              <span className="font-bold text-[#0A2540]">您專心做現場服務，</span>收錢、接單、客服、行銷這些繁瑣事務我們全包辦。500+ 網紅合作導流、AI 智能接單，讓您的民宿營收翻倍成長。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <a href={LINE_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-btn"
                className="bg-[#FF6B00] text-white hover:bg-[#E65A00] transition-all duration-300 font-bold rounded-full px-6 md:px-8 py-3.5 md:py-4 flex items-center justify-center gap-2 btn-lift animate-pulse-glow text-sm md:text-base">
                <MessageCircle className="w-5 h-5" />
                預約免費諮詢
                <ChevronRight className="w-5 h-5" />
              </a>
              <a href="#pricing" data-testid="hero-secondary-btn"
                className="bg-[#0A2540] text-white hover:bg-[#163859] transition-all duration-300 font-bold rounded-full px-6 md:px-8 py-3.5 md:py-4 flex items-center justify-center gap-2 text-sm md:text-base">
                查看方案
              </a>
            </div>

            <div className="grid grid-cols-3 gap-2 md:gap-6 pt-2 md:pt-4">
              <div className="text-xs md:text-sm text-[#475569]">
                <span className="font-black text-2xl md:text-3xl text-[#FF6B00] block">500+</span>
                <span className="block leading-tight">網紅合作導流</span>
              </div>
              <div className="text-xs md:text-sm text-[#475569] md:border-l md:border-slate-200 md:pl-6">
                <span className="font-black text-2xl md:text-3xl text-[#FF6B00] block">60-95%</span>
                <span className="block leading-tight">專業客服滿房率</span>
              </div>
              <div className="text-xs md:text-sm text-[#475569] md:border-l md:border-slate-200 md:pl-6">
                <span className="font-black text-2xl md:text-3xl text-[#FF6B00] block">13hrs</span>
                <span className="block leading-tight">每日專人接單</span>
              </div>
            </div>
          </div>
          
          <div className="relative animate-fade-in-up stagger-2">
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMAGES.hero} alt="現代化民宿" className="w-full h-auto object-cover" />
              {/* Desktop: floating glass card on image */}
              <div className="hidden md:block absolute bottom-6 left-6 glass-card rounded-2xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FF6B00] flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-[#0A2540]">160%</div>
                    <div className="text-sm text-[#475569]">同期營收增長</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Mobile: data bar below the image */}
            <div className="md:hidden mt-3 flex items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-md border border-slate-100">
              <div className="w-10 h-10 rounded-full bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-baseline gap-2 flex-1">
                <span className="text-2xl font-black text-[#FF6B00] leading-none">160%</span>
                <span className="text-sm text-[#475569]">同期營收增長</span>
              </div>
            </div>
            <div className="hidden md:block absolute -top-6 -right-6 w-24 h-24 bg-[#FF6B00]/10 rounded-full"></div>
            <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-[#0047FF]/10 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
